import { Box, Paper, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import aiAgent1Image from '../../assets/agent1.svg';
import aiAgent2Image from '../../assets/agent2.svg';
import aiAgent3Image from '../../assets/agent3.svg';
import StyledCTAButton from '../shared/Button/StyledCTAButton';
import StyledWelcomeAgentImage1 from './StyledWelcomeAgentImage1';
import StyledWelcomeAgentImage2 from './StyledWelcomeAgentImage2';
import StyledWelcomeAgentImage3 from './StyledWelcomeAgentImage3';
import { useContext } from 'react';
import AuthContext from '../../contexes/AuthContext';
import useCompany from '../../hooks/useCompany';
import Modal from '../shared/Modal/Modal';
import { useTranslation } from '../../hooks/useTranslation';

const WelcomeScreen = () => {
  const { currentUser } = useContext(AuthContext);
  const { companyDetails } = useCompany(currentUser);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onStartButtonClick = () => {
    navigate('/agents/new');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: { xs: 3, md: 4 },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0px 10px 28px rgba(15, 23, 42, 0.08)',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          background:
            'radial-gradient(1200px 340px at -10% -25%, rgba(37, 99, 235, 0.08), transparent 60%)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={{ xs: 2.5, md: 3.5 }}
          alignItems="stretch"
        >
          <Stack
            spacing={3.25}
            width={{ xs: '100%', md: '54%' }}
            minHeight={{ md: 520 }}
            sx={{ justifyContent: 'center', pr: { md: 1 } }}
          >
            <Typography
              component="h1"
              sx={{
                m: 0,
                maxWidth: 560,
                fontSize: { xs: '2.5rem', md: '3.7rem' },
                lineHeight: { xs: 1.12, md: 1.08 },
                letterSpacing: '-0.02em',
                fontWeight: 700,
                color: '#0F172A',
              }}
            >
              {t("Welcome! Let's Create Your First AI Agent 🎉")}
            </Typography>
            <Typography
              sx={{
                m: 0,
                maxWidth: 520,
                fontSize: { xs: '1.1rem', md: '1.9rem' },
                lineHeight: 1.45,
                fontWeight: 400,
                color: '#334155',
              }}
            >
              {t(
                'Get your AI agent working quickly to provide great service and help your business grow. Start now and see the difference!'
              )}
            </Typography>
            <Stack minHeight="60px" width={{ xs: '100%', sm: 280 }}>
              {companyDetails &&
                (companyDetails?.name ? (
                  <StyledCTAButton
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={onStartButtonClick}
                  >
                    {t('Start')}
                  </StyledCTAButton>
                ) : (
                  <Modal
                    mainPromptText={t('Company details missing')}
                    denyText={t('Cancel')}
                    confirmText={t('Setup up company details')}
                    confirmButtonColor="primary"
                    description={t(
                      'In order to create an agent please fill up the company information.'
                    )}
                    showButton={true}
                    openModalButtonVariant="contained"
                    openModalButtonColor="primary"
                    openModalButtonText={t('START')}
                    callback={() => navigate('/settings')}
                    openModalButtonSize="large"
                  />
                ))}
            </Stack>
          </Stack>
          <Stack
            width={{ xs: '100%', md: '46%' }}
            position="relative"
            gap={{ xs: '1rem', sm: '1.5rem', md: '2rem' }}
            direction={{ xs: 'column', sm: 'row' }}
            flexWrap="wrap"
            minHeight={{ md: 520 }}
            justifyContent="center"
            sx={{
              borderRadius: { md: 3 },
              background:
                'radial-gradient(420px 220px at 80% 15%, rgba(37, 99, 235, 0.08), transparent 70%)',
            }}
          >
            <StyledWelcomeAgentImage1
              src={aiAgent1Image}
              alt={t('AI agent 1')}
            />
            <StyledWelcomeAgentImage2
              src={aiAgent2Image}
              alt={t('AI agent 2')}
            />
            <StyledWelcomeAgentImage3
              src={aiAgent3Image}
              alt={t('AI agent 3')}
            />
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export default WelcomeScreen;
